import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useContext,
  useLayoutEffect,
} from "react";
import { shareInFlightRequest } from "../lib/sharedApiRequest";
import {
  Typography,
  Container,
  Box,
  Card,
  CardContent,
  Stack,
  Button,
  TextField,
  Avatar,
  Divider,
  IconButton,
  MenuList,
  MenuItem,
  Paper,
  Popper,
  ClickAwayListener,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Tooltip,
  useTheme,
  Alert,
  CircularProgress,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useSearchParams } from "react-router-dom";
import { apiClient } from "../lib/apiClient";
import { fetchUserAttributes } from "aws-amplify/auth";
import { UserContext } from "../context/UserContext";
import { API_ENDPOINTS } from "../constants";
import { getKanaForName } from "../components/common/utils/nameKanaMap";
import ConfirmDialog from "../components/common/ConfirmDialog";
import AlertDialog from "../components/common/AlertDialog";
import MentionPickerPopper from "../components/WeeklyThread/MentionPicker/MentionPickerPopper";
import { MentionUser } from "../components/WeeklyThread/WeeklyThreadTypes";
import { displayUserInitial, emailEq, normEmail, normStr } from "../components/WeeklyThread/WeeklyThreadUtils";
import { NoticeStatusHelpLink } from "../components/WeeklyThread/NoticeStatus/NoticeStatusHelp";
import {
  ACTIVE_COLOR,
  getNoticeStatus,
  renderStatusCheck,
  renderStatusDetail,
} from "../components/WeeklyThread/NoticeStatus/NoticeStatusRender";
import "./WeeklyThreadWindow.css";

/* =========================================================
 * 型
 * ======================================================= */
type ThreadNode = {
  id: number;
  user: string;
  text: string;
  ts: number;
  mail: string; // 投稿者メール（権限判定に使用）
  replies: ThreadNode[];
  chatId?: number | null; // どの親コメント(chat_id)に属する返信か
  noticeUsers?: NoticeUser[];
};

type WrPayload = {
  wr_id: number | string;
  title: string;
  body: string;
  department?: string;
  team?: string;
  reporter?: string;
  reporter_mail?: string;   // 週報発行者のメールアドレス（create_by_user_id）。返信先の判定に使う
  updated_at?: string;
};

type WrApiItem = {
  wr_id: number;
  title?: string | null;
  body?: string | null;
  department?: number | string | null;
  team?: number | string | null;
  create_by_user_id?: string | null;
  createByUserId?: string | null;
  create_by?: string | null;
  create_dt?: string | null;
  update_by?: string | null;
  update_dt?: string | null;
};

type DirectoryUser = {
  mail_address: string;
  last_name: string;
  first_name: string;
  department?: string | null;
  team?: string | null;
};

type DirectoryResponse = {
  users: DirectoryUser[];
  total: number;
};

type WeeklyReportThreadOut = {
  id: number;
  wr_id: number;
  mail_address: string;
  last_name?: string | null;
  first_name?: string | null;
  body: string;
  is_deleted: boolean;
  created_at: string;
  updated_at: string;
  notice_users?: NoticeUser[];
};

type WeeklyReportThreadReplyOut = {
  id: number;
  wr_id: number;
  chat_id: number; // 親コメントID
  mail_address: string;
  last_name?: string | null;
  first_name?: string | null;
  body: string;
  is_deleted: boolean;
  created_at: string;
  updated_at: string;
  notice_users?: NoticeUser[];
};

type NoticeUser = {
  user: string;
  mail_address: string;
  mail_status: string;
  confirm: number;
};

type HistoryUser = {
  mail_address: string;
  last_name?: string | null;
  first_name?: string | null;
  last_name_kana?: string | null;
  first_name_kana?: string | null;
  department?: string | null;
};

type EditingTarget =
  | { kind: "chat"; id: number; origin: "main" | "thread" } // 親コメント
  | { kind: "reply"; id: number; chatId: number; origin: "thread" }; // 返信

/* メニューで保持するターゲット（id と chatId の組み合わせ） */
type MenuTarget = { id: number; chatId: number | null };

// スレッド返信の際の通知先データソース
type MentionSource = Pick<ThreadNode, "mail" | "user" | "noticeUsers">;

// 既定の返信先を組み立てる際の入力（メールアドレス＋表示名）
type MentionSourceUser = { mail?: string; user?: string };


/* =========================================================
 * ユーティリティ
 * ======================================================= */
const fmt = (ts: number) => {
  const d = new Date(ts);
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const hh = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  return `${mm}/${dd} ${hh}:${mi}`;
};

const scanMaxId = (nodes: ThreadNode[]) => {
  let maxId = 0;
  const walk = (n: ThreadNode) => {
    if (typeof n.id === "number") maxId = Math.max(maxId, n.id);
    (n.replies || []).forEach(walk);
  };
  (nodes || []).forEach(walk);
  return maxId;
};

// chatId を見て「この親の返信だけ」を数える
const countReplies = (node: ThreadNode, rootId?: number): number => {
  const targetChatId = rootId ?? node.id;
  let count = 0;

  const walk = (n: ThreadNode) => {
    (n.replies || []).forEach((r) => {
      const belongsChatId = r.chatId ?? targetChatId;
      if (belongsChatId === targetChatId) {
        count += 1;
      }
      walk(r);
    });
  };

  walk(node);
  return count;
};

const nameCacheKey = (email: string) =>
  `wr-thread:display-name:v1:${String(email || "").toLowerCase()}`;

const THREAD_CACHE_INDEX_KEY = "wr-thread:cache-index:v1";
const THREAD_CACHE_MAX_WR_ENTRIES = 20;

const parseThreadCacheKey = (
  key: string
): { wrId: string; suffix: "messages" | "payload" | "fetched" } | null => {
  const m = key.match(/^wr-thread:([^:]+):(messages|payload|fetched)$/);
  if (!m) return null;
  return {
    wrId: String(m[1]),
    suffix: m[2] as "messages" | "payload" | "fetched",
  };
};

const readThreadCacheIndex = (): Record<string, number> => {
  try {
    const raw = localStorage.getItem(THREAD_CACHE_INDEX_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    const out: Record<string, number> = {};
    Object.entries(parsed || {}).forEach(([k, v]) => {
      if (typeof v === "number" && Number.isFinite(v)) out[k] = v;
    });
    return out;
  } catch {
    return {};
  }
};

const writeThreadCacheIndex = (index: Record<string, number>) => {
  try {
    localStorage.setItem(THREAD_CACHE_INDEX_KEY, JSON.stringify(index));
  } catch {
    // ignore
  }
};

const touchThreadCacheWrId = (wrId: string) => {
  const index = readThreadCacheIndex();
  index[wrId] = Date.now();
  writeThreadCacheIndex(index);
};

const removeThreadCacheByWrId = (wrId: string) => {
  try {
    localStorage.removeItem(`wr-thread:${wrId}:messages`);
    localStorage.removeItem(`wr-thread:${wrId}:payload`);
    localStorage.removeItem(`wr-thread:${wrId}:fetched`);
  } catch {
    // ignore
  }
};

const pruneThreadCache = (currentWrId?: string) => {
  const wrIdsInStorage = new Set<string>();
  try {
    for (let i = 0; i < localStorage.length; i += 1) {
      const k = localStorage.key(i);
      if (!k) continue;
      const parsed = parseThreadCacheKey(k);
      if (!parsed) continue;
      wrIdsInStorage.add(parsed.wrId);
    }
  } catch {
    return;
  }

  const index = readThreadCacheIndex();
  wrIdsInStorage.forEach((wrId) => {
    if (typeof index[wrId] !== "number") {
      index[wrId] = 0;
    }
  });
  Object.keys(index).forEach((wrId) => {
    if (!wrIdsInStorage.has(wrId)) {
      delete index[wrId];
    }
  });

  const sorted = Object.entries(index).sort((a, b) => b[1] - a[1]);
  const keep = new Set(
    sorted.slice(0, THREAD_CACHE_MAX_WR_ENTRIES).map(([wrId]) => wrId)
  );
  if (currentWrId) keep.add(currentWrId);

  wrIdsInStorage.forEach((wrId) => {
    if (keep.has(wrId)) return;
    removeThreadCacheByWrId(wrId);
    delete index[wrId];
  });

  writeThreadCacheIndex(index);
};

const setNameCache = (email: string, display: string) => {
  try {
    localStorage.setItem(
      nameCacheKey(email),
      JSON.stringify({ ts: Date.now(), value: display })
    );
  } catch {
    // ignore
  }
};

const getNameCache = (email: string): string | null => {
  try {
    const raw = localStorage.getItem(nameCacheKey(email));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { ts: number; value: string };
    return parsed?.value ?? null;
  } catch {
    return null;
  }
};

const DEFAULT_THREAD_WIDTH = 560;

// 返信先（通知先）の上限件数（50件以上は送信不可）
const MAX_MENTIONS = 49;
// 返信先ポップアップに表示する履歴の件数（直近に指定した順、ログインユーザー自身は除く）
const MENTION_HISTORY_LIMIT = 6;


/* -------------------------------------------------------
 * 判別可能ユニオンのためのガード（reply だけを狙う）
 * ----------------------------------------------------- */
const isReplyLike = (
  t: unknown
): t is { kind: "reply"; id: number; chatId: number } => {
  return !!t && typeof t === "object" && (t as any).kind === "reply";
};

/* -------------------------------------------------------
 * ノード探索系ユーティリティ
 * ----------------------------------------------------- */

/** 親コメント（トップレベル）だけを対象に探す */
const findRootNodeById = (
  ns: ThreadNode[],
  targetId: number
): { node: ThreadNode; parent: null } | null => {
  const node = ns.find((n) => n.id === targetId);
  if (!node) return null;
  return { node, parent: null };
};

/** 全ツリーを対象に親付きで探す（従来の関数） */
const findNodeWithParent = (
  ns: ThreadNode[],
  targetId: number,
  parent: ThreadNode | null = null
): { node: ThreadNode; parent: ThreadNode | null } | null => {
  for (const n of ns) {
    if (n.id === targetId) return { node: n, parent };
    const childResult = findNodeWithParent(n.replies, targetId, n);
    if (childResult) return childResult;
  }
  return null;
};

/** chatId のルート配下だけを対象に targetId を探す */
const findNodeWithParentInChat = (
  ns: ThreadNode[],
  chatId: number,
  targetId: number
): { node: ThreadNode; parent: ThreadNode | null } | null => {
  const root = ns.find((n) => n.id === chatId);
  if (!root) return null;

  const walk = (
    n: ThreadNode,
    parent: ThreadNode | null
  ): { node: ThreadNode; parent: ThreadNode | null } | null => {
    if (n.id === targetId) return { node: n, parent };
    for (const r of n.replies) {
      const hit = walk(r, n);
      if (hit) return hit;
    }
    return null;
  };

  // ここでは「返信のみ」を探したいので root 自体は対象にしない
  for (const r of root.replies) {
    const hit = walk(r, root);
    if (hit) return hit;
  }
  return null;
};

/* =========================================================
 * 本体
 * ======================================================= */
const WeeklyThreadWindow: React.FC = () => {
  const { palette } = useTheme();
  const isDark = palette.mode === "dark";
  // ブラウザ標準の alert() の代わりに表示するエラーダイアログ
  const [alertMessage, setAlertMessage] = useState<string | null>(null);
  const showAlert = (message: string) => setAlertMessage(message);

  useEffect(() => {
    // この画面を開いているあいだだけ「スレッド専用モード」にする
    document.body.classList.add("thread-window-page");

    return () => {
      document.body.classList.remove("thread-window-page");
    };
  }, []);

  // 画面上部のヘッダー分を自動で差し引いて、
  // メッセージ入力欄までが常にビューポート内に入るようにする
  const rootRef = useRef<HTMLDivElement | null>(null);
  const openedUrlRef = useRef<string>("");
  const [containerHeight, setContainerHeight] = useState<number | null>(null);

  useLayoutEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const updateHeight = () => {
      if (!rootRef.current) return;

      const rect = rootRef.current.getBoundingClientRect();
      // ヘッダーや上の余白を含めた、画面上端からの距離
      const topOffset = Math.max(rect.top, 0);
      const available = window.innerHeight - topOffset;

      // 利用可能な高さが 0 以下になることはほぼないが、安全のため fallback
      const nextHeight = available > 0 ? available : window.innerHeight;
      setContainerHeight(nextHeight);
    };

    updateHeight();

    // リサイズとスクロールに追従
    window.addEventListener("resize", updateHeight);
    window.addEventListener("scroll", updateHeight, true);

    return () => {
      window.removeEventListener("resize", updateHeight);
      window.removeEventListener("scroll", updateHeight, true);
    };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    // ページ初回表示時のURLを保持
    openedUrlRef.current = window.location.href;
  }, []);

  const [params] = useSearchParams();
  const wrId = params.get("wr_id") ?? "";
  const key = params.get("key") ?? "";
  const initialChatId = params.get("chat_id") ?? ""; // メールのリンクから開いたときのスレッド

  const authUser = useContext(UserContext);
  const [payload, setPayload] = useState<WrPayload | null>(null);
  const [seq, setSeq] = useState<number>(0);
  const [messages, setMessages] = useState<ThreadNode[]>([]);
  // DB からの初回取得の結果（キャッシュ復元分ではなく最新の内容でスレッドを自動オープンするため）
  //  ※ 定期リロードでは更新しない（初回が失敗した場合は自動オープンしない）
  const [initialFetchStatus, setInitialFetchStatus] =
    useState<"pending" | "success" | "failed">("pending");
  const [allUserInfo, setAllUserInfo] = useState<any[]>([]);
  // 全ユーザー取得の読み込み中・失敗状態と、再読み込み用のキー
  const [userInfoLoading, setUserInfoLoading] = useState(true);   // マウント直後から取得するため true で開始
  const [userInfoLoadError, setUserInfoLoadError] = useState(false);
  const [userInfoReloadKey, setUserInfoReloadKey] = useState(0);
  const reloadAllUsers = React.useCallback(() => {
    setUserInfoReloadKey((k) => k + 1);
  }, []);

  const [currentUser, setCurrentUser] = useState<string>("");
  const [userEmail, setUserEmail] = useState<string>("");
  const [userLastName, setUserLastName] = useState<string>("");
  const [userFirstName, setUserFirstName] = useState<string>("");

  useEffect(() => {
    let abort = false;

    const loadAllUsers = async () => {
      setUserInfoLoading(true);
      try {
        const res = await shareInFlightRequest(
          'get-all-user-info:all',
          () => apiClient.get(API_ENDPOINTS.GET_ALL_USER_INFO),
        );
        if (abort) return;
        const data = res?.data;
        const list = Array.isArray(data)
          ? data
          : Array.isArray(data?.users)
          ? data.users
          : Array.isArray(data?.Users)
          ? data.Users
          : Array.isArray(data?.list)
          ? data.list
          : Array.isArray(data?.data)
          ? data.data
          : [];
        setAllUserInfo(list);
        setUserInfoLoadError(false);
      } catch (err) {
        console.warn("GET /get_all_user_info failed:", err);
        if (!abort) {
          setAllUserInfo([]);
          setUserInfoLoadError(true);
        }
      } finally {
        if (!abort) setUserInfoLoading(false);
      }
    };

    loadAllUsers();
    return () => {
      abort = true;
    };
  }, [userInfoReloadKey]);

  const mentionCandidates = useMemo<MentionUser[]>(() => {
    const list = (allUserInfo || [])
      .map((u) => {
        const status = normStr(u?.status);
        if (status && status.toUpperCase() === "N") return null;

        const email =
          normStr(
            u?.mail_address ??
              u?.mailAddress ??
              u?.email ??
              u?.mail ??
              u?.user_id ??
              u?.userId ??
              u?.id ??
              u?.value
          ) || "";
        if (!email) return null;

        const last = normStr(u?.last_name ?? u?.lastName ?? "");
        const first = normStr(u?.first_name ?? u?.firstName ?? "");
        const name = normStr(
          u?.name ??
            u?.user_name ??
            u?.userName ??
            u?.display_name ??
            u?.displayName ??
            u?.full_name ??
            u?.fullName ??
            u?.label ??
            ""
        );

        const display =
          last || first ? `${last} ${first}`.trim() : name || email;

        const kanaLast = normStr(
          u?.last_name_kana ?? u?.lastNameKana ?? ""
        );
        const kanaFirst = normStr(
          u?.first_name_kana ?? u?.firstNameKana ?? ""
        );
        const kanaCombined = [kanaLast, kanaFirst]
          .filter(Boolean)
          .join(" ")
          .trim();
        const kanaRaw = normStr(
          u?.phonetic_name ??
            u?.phoneticName ??
            u?.kana ??
            u?.name_kana ??
            u?.display_kana ??
            ""
        );
        const kana =
          kanaCombined || kanaRaw || getKanaForName(display) || "";

        const department = normStr(u?.department ?? "");

        return { email, display, kana, department } as MentionUser;
      })
      .filter(Boolean) as MentionUser[];

    const seen = new Set<string>();
    return list
      .filter((u) => {
        const key = u.email.toLowerCase();
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .sort((a, b) => a.display.localeCompare(b.display, "ja"));
  }, [allUserInfo]);

  /* -----------------------------------------------------
   * ログイン中ユーザー email/姓名 解決
   * --------------------------------------------------- */
  useEffect(() => {
    let abort = false;

    const resolveAndSetName = async () => {
      try {
        let email = "";
        const loginId: string =
          (authUser as any)?.signInDetails?.loginId ??
          (authUser as any)?.username ??
          "";

        if (loginId && loginId.includes("@")) {
          email = String(loginId);
        } else {
          try {
            const attrs = await fetchUserAttributes();
            email = String((attrs as any)?.email ?? "");
          } catch {
            email = String(loginId || "");
          }
        }
        email = email.trim();

        if (!email) {
          if (!abort) {
            setCurrentUser("");
            setUserEmail("");
            setUserLastName("");
            setUserFirstName("");
          }
          return;
        }

        if (!abort) {
          setUserEmail(email);
        }

        const cachedDisplay = getNameCache(email);
        if (cachedDisplay && !abort) {
          setCurrentUser(cachedDisplay);
          const parts = cachedDisplay.split(/\s+/);
          setUserLastName(parts[0] ?? "");
          setUserFirstName(parts.slice(1).join(" ") ?? "");
        }

        try {
          const { data } = await apiClient.get<DirectoryResponse>(
            "/user-info",
            { params: { mail: email } }
          );

          const list = Array.isArray(data?.users) ? data.users : [];
          const hit = list[0] ?? null;

          let display = email;
          let lastName = "";
          let firstName = "";

          if (hit) {
            lastName = hit.last_name ?? "";
            firstName = hit.first_name ?? "";
            display =
              lastName || firstName
                ? `${lastName ?? ""} ${firstName ?? ""}`.trim()
                : email;
          }

          if (!abort) {
            setCurrentUser(display);
            setUserLastName(lastName || display);
            setUserFirstName(firstName || "");
            setNameCache(email, display);
          }
        } catch (err) {
          console.warn("GET /user-info failed:", err);
        }
      } catch (e) {
        console.error("resolve name error:", e);
        if (!abort) {
          setCurrentUser("");
          setUserLastName("");
          setUserFirstName("");
        }
      }
    };

    resolveAndSetName();
    return () => {
      abort = true;
    };
  }, [authUser]);

  const storageKeyMessages = useMemo(
    () => `wr-thread:${wrId || "unknown"}:messages`,
    [wrId]
  );
  const storageWrCacheId = useMemo(() => String(wrId || "unknown"), [wrId]);
  const storageKeyPayload = useMemo(
    () => `wr-thread:${wrId || "unknown"}:payload`,
    [wrId]
  );
  const storageKeyFetched = useMemo(
    () => `wr-thread:${wrId || "unknown"}:fetched`,
    [wrId]
  );

  useEffect(() => {
    touchThreadCacheWrId(storageWrCacheId);
    pruneThreadCache(storageWrCacheId);
  }, [storageWrCacheId]);

  /* -----------------------------------------------------
   * 受け渡し（メイン → 別タブ：key）を恒久保存
   * --------------------------------------------------- */
  useEffect(() => {
    if (!key) return;
    try {
      const raw = localStorage.getItem(key);
      if (raw) {
        const data = JSON.parse(raw) as WrPayload;
        setPayload(data);
        localStorage.setItem(storageKeyPayload, JSON.stringify(data));
      }
    } catch (e) {
      console.error("スレッドペイロードの読込失敗:", e);
    } finally {
      try {
        localStorage.removeItem(key);
      } catch {
        // ignore
      }
    }
  }, [key, storageKeyPayload]);

  /* -----------------------------------------------------
   * 初期ロード：messages / payload を復元
   * --------------------------------------------------- */
  useEffect(() => {
    try {
      const raw = localStorage.getItem(storageKeyMessages);
      if (raw) {
        const parsed = JSON.parse(raw) as ThreadNode[];
        setMessages(parsed);
        setSeq(scanMaxId(parsed));
      } else {
        setMessages([]);
        setSeq(0);
      }
    } catch (e) {
      console.error("スレッドの復元に失敗:", e);
      setMessages([]);
      setSeq(0);
    }

    try {
      const rawP = localStorage.getItem(storageKeyPayload);
      if (rawP) {
        const parsedP = JSON.parse(rawP) as WrPayload;
        setPayload(parsedP);
      }
    } catch (e) {
      console.error("payload の復元に失敗:", e);
    }
    touchThreadCacheWrId(storageWrCacheId);
    pruneThreadCache(storageWrCacheId);
  }, [storageKeyMessages, storageKeyPayload, storageWrCacheId]);

  /* -----------------------------------------------------
   * 別タブ更新の同期
   * --------------------------------------------------- */
  useEffect(() => {
    const onStorage = (ev: StorageEvent) => {
      if (ev.key === storageKeyMessages) {
        try {
          if (ev.newValue == null) {
            setMessages([]);
            setSeq(0);
          } else {
            const next = JSON.parse(ev.newValue) as ThreadNode[];
            setMessages(next);
            setSeq(scanMaxId(next));
          }
        } catch (e) {
          console.error("storage(messages) 反映失敗:", e);
        }
      }
      if (ev.key === storageKeyPayload) {
        try {
          if (ev.newValue == null) {
            setPayload(null);
          } else {
            const next = JSON.parse(ev.newValue) as WrPayload;
            setPayload(next);
          }
        } catch (e) {
          console.error("storage(payload) 反映失敗:", e);
        }
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [storageKeyMessages, storageKeyPayload]);

  /* -----------------------------------------------------
   * 変更があれば都度 永続化
   * --------------------------------------------------- */
  useEffect(() => {
    try {
      localStorage.setItem(storageKeyMessages, JSON.stringify(messages));
      touchThreadCacheWrId(storageWrCacheId);
      pruneThreadCache(storageWrCacheId);
    } catch (e) {
      console.error("スレッドの保存に失敗:", e);
    }
  }, [messages, storageKeyMessages, storageWrCacheId]);

  useEffect(() => {
    if (!payload) return;
    try {
      localStorage.setItem(storageKeyPayload, JSON.stringify(payload));
      touchThreadCacheWrId(storageWrCacheId);
      pruneThreadCache(storageWrCacheId);
    } catch (e) {
      console.error("payload の保存に失敗:", e);
    }
  }, [payload, storageKeyPayload, storageWrCacheId]);

  /* -----------------------------------------------------
   * POST 用 wr_id を正規化
   * --------------------------------------------------- */
  const resolvedWrId = useMemo(() => {
    const raw = (payload?.wr_id ?? wrId) as string | number | null;
    if (raw == null || raw === "") return null;
    const n = Number(raw);
    if (!Number.isFinite(n)) return null;
    return n;
  }, [payload, wrId]);

  /* -----------------------------------------------------
   * 初回だけ /weekly-report-search で payload を上書き
   * --------------------------------------------------- */
  useEffect(() => {
    if (!wrId) return;
    const already = localStorage.getItem(storageKeyFetched) === "1";
    if (already) return;

    const controller = new AbortController();

    const load = async () => {
      try {
        const params: any = { wr_id: wrId };

        const res = await apiClient.get<WrApiItem[]>("/weekly-report-search", {
          params,
          signal: controller.signal as any,
        });

        const data = res.data;
        const item = (Array.isArray(data) ? data : [])[0];
        if (!item) return;

        const next: WrPayload = {
          wr_id: item.wr_id,
          title: String(item.title ?? ""),
          body: String(item.body ?? ""),
          department:
            item.department == null ? undefined : String(item.department),
          team: item.team == null ? undefined : String(item.team),
          reporter:
            normStr(
              item.create_by_user_id ?? item.createByUserId ?? item.create_by
            ) || undefined,
          reporter_mail:
            normStr(item.create_by_user_id ?? item.createByUserId) || undefined,
          updated_at: item.update_dt ?? item.create_dt ?? undefined,
        };

        setPayload(next);
        localStorage.setItem(storageKeyPayload, JSON.stringify(next));
        localStorage.setItem(storageKeyFetched, "1");
        touchThreadCacheWrId(storageWrCacheId);
        pruneThreadCache(storageWrCacheId);
      } catch (e: any) {
        if (e?.name === "CanceledError" || e?.name === "AbortError") return;
        console.error("weekly-report-search 取得失敗:", e);
      }
    };

    load();
    return () => {
      controller.abort();
    };
  }, [wrId, storageKeyFetched, storageKeyPayload, storageWrCacheId]);

  /* -----------------------------------------------------
   * ID 採番（ローカル用）
   * --------------------------------------------------- */
  const nextId = () => {
    const newId = seq + 1;
    setSeq(newId);
    return newId;
  };

  const addTopMessage = (
    user: string,
    text: string,
    ts?: number,
    idFromApi?: number,
    mail?: string,
    noticeUsers?: NoticeUser[]
  ) => {
    const nodeId =
      typeof idFromApi === "number" && Number.isFinite(idFromApi)
        ? idFromApi
        : nextId();

    const node: ThreadNode = {
      id: nodeId,
      user,
      text,
      ts: ts ?? Date.now(),
      mail: mail ?? "",
      replies: [],
      chatId: null, // 親は chatId なし
      noticeUsers: Array.isArray(noticeUsers) ? noticeUsers : [],
    };
    setMessages((prev) => [...prev, node]);
    return node;
  };

  const addReply = (
    parentId: number,
    user: string,
    text: string,
    ts?: number,
    idFromApi?: number,
    mail?: string,
    noticeUsers?: NoticeUser[]
  ) => {
    const replyId =
      typeof idFromApi === "number" && Number.isFinite(idFromApi)
        ? idFromApi
        : nextId();

    const walk = (ns: ThreadNode[]): ThreadNode[] =>
      ns.map((n) => {
        if (n.id === parentId) {
          const reply: ThreadNode = {
            id: replyId,
            user,
            text,
            ts: ts ?? Date.now(),
            mail: mail ?? "",
            replies: [],
            chatId: parentId, // どの親(chat_id)の返信かを明示
            noticeUsers: Array.isArray(noticeUsers) ? noticeUsers : [],
          };
          return { ...n, replies: [...n.replies, reply] };
        }
        return { ...n, replies: walk(n.replies) };
      });
    setMessages((prev) => walk(prev));
  };

  /* -----------------------------------------------------
   * DB から親コメントと返信を取得
   * --------------------------------------------------- */
  const fetchThreadsAndReplies = React.useCallback(async () => {
    if (!resolvedWrId) {
      return;
    }

    try {
      const readerParam = userEmail ? { reader: userEmail } : {};
      const [threadsRes, repliesRes] = await Promise.all([
        apiClient.get<WeeklyReportThreadOut[]>("/weekly-report-thread", {
          params: { wr_id: resolvedWrId, ...readerParam },
        }),
        apiClient.get<WeeklyReportThreadReplyOut[]>(
          "/weekly-report-thread-reply",
          { params: { wr_id: resolvedWrId, ...readerParam } }
        ),
      ]);

      const threadRows = (threadsRes.data ?? []).filter(
        (r) => !r.is_deleted
      );
      const replyRows = (repliesRes.data ?? []).filter(
        (r) => !r.is_deleted
      );

      const nodes: ThreadNode[] = threadRows.map((r) => ({
        id: r.id,
        user:
          (r.last_name || r.first_name)
            ? `${r.last_name ?? ""} ${r.first_name ?? ""}`.trim()
            : r.mail_address,
        text: r.body,
        ts: r.created_at ? new Date(r.created_at).getTime() : Date.now(),
        mail: r.mail_address ?? "",
        replies: [],
        chatId: null, // 親は chatId なし
        noticeUsers: Array.isArray(r.notice_users)
          ? r.notice_users
          : [],
      }));

      const replyMap = new Map<number, ThreadNode[]>();

      replyRows.forEach((r) => {
        const replyNode: ThreadNode = {
          id: r.id,
          user:
            (r.last_name || r.first_name)
              ? `${r.last_name ?? ""} ${r.first_name ?? ""}`.trim()
              : r.mail_address,
          text: r.body,
          ts: r.created_at ? new Date(r.created_at).getTime() : Date.now(),
          mail: r.mail_address ?? "",
          replies: [],
          chatId: r.chat_id, // chat_id を保持
          noticeUsers: Array.isArray(r.notice_users)
            ? r.notice_users
            : [],
        };
        const list = replyMap.get(r.chat_id) ?? [];
        list.push(replyNode);
        replyMap.set(r.chat_id, list);
      });

      const merged: ThreadNode[] = nodes.map((n) => {
        const replies = replyMap.get(n.id) ?? [];
        const sortedReplies = replies
          .slice()
          .sort((a, b) => a.ts - b.ts);
        return { ...n, replies: sortedReplies };
      });

      setMessages(merged);
      setSeq(scanMaxId(merged));
      setInitialFetchStatus((prev) => (prev === "pending" ? "success" : prev));
    } catch (e) {
      console.error("weekly-report-thread / reply 取得失敗:", e);
      setInitialFetchStatus((prev) => (prev === "pending" ? "failed" : prev));
    }
  }, [resolvedWrId, setMessages, setSeq, userEmail]);

  useEffect(() => {
    // 初回ロード
    void fetchThreadsAndReplies();

    // 1分ごとに自動リロード
    const intervalId = window.setInterval(() => {
      void fetchThreadsAndReplies();
    }, 60_000); // 60秒

    return () => {
      window.clearInterval(intervalId);
    };
  }, [fetchThreadsAndReplies]);

  /* -----------------------------------------------------
   * 編集・削除メニュー（Popper 版）
   * --------------------------------------------------- */
  const [menuTarget, setMenuTarget] = useState<MenuTarget | null>(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
  const isMenuOpen = Boolean(menuAnchorEl);
  const lastMenuTriggerRef = useRef<HTMLElement | null>(null);
  const menuListRef = useRef<HTMLUListElement | null>(null);

  /* メニュー開閉に使う編集可否判定（chatId 対応＆親はルートのみ） */
  const canEditById = (id: number, chatId?: number | null): boolean => {
    const found =
      chatId != null
        ? findNodeWithParentInChat(messages, chatId, id)
        : findRootNodeById(messages, id); // 親コメントはトップレベルだけ
    if (!found) return false;
    return emailEq(found.node.mail, userEmail);
  };

  /* chatId を受け取れるように修正：右ペインの返信は常に root の chatId を渡す */
  const handleOpenMessageMenu = (
    event: React.MouseEvent<HTMLElement>,
    messageId: number,
    chatId?: number | null
  ) => {
    if (!canEditById(messageId, chatId)) return;
    setMenuAnchorEl(event.currentTarget as HTMLElement);
    setMenuTarget({ id: messageId, chatId: chatId ?? null });
    lastMenuTriggerRef.current = event.currentTarget as HTMLElement;
  };

  const handleCloseMessageMenu = () => {
    const active = document.activeElement as HTMLElement | null;
    if (active && menuListRef.current?.contains(active)) {
      active.blur();
    }
    setMenuAnchorEl(null);
    setMenuTarget(null);
    requestAnimationFrame(() => {
      if (lastMenuTriggerRef.current) {
        lastMenuTriggerRef.current.focus();
      } else {
        topInputRef.current?.focus();
      }
    });
  };

  /* -----------------------------------------------------
   * 編集状態
   * --------------------------------------------------- */
  const [editingTarget, setEditingTarget] = useState<EditingTarget | null>(
    null
  );
  const [editingText, setEditingText] = useState<string>("");

  const getRootChatId = (targetId: number): number | null => {
    let currentId = targetId;
    while (true) {
      const found = findNodeWithParent(messages, currentId);
      if (!found) return null;
      if (found.parent === null) return found.node.id;
      currentId = found.parent.id;
    }
  };

  const startEditMessage = (messageId: number) => {
    const contextChatId = menuTarget?.chatId ?? null;
    const origin: "main" | "thread" =
      contextChatId == null ? "main" : "thread";

    const found =
      contextChatId != null
        ? findNodeWithParentInChat(messages, contextChatId, messageId)
        : findRootNodeById(messages, messageId);

    if (!found) return;

    if (!emailEq(found.node.mail, userEmail)) {
      handleCloseMessageMenu();
      return;
    }

    const isRootMessage = found.parent === null;

    handleCloseMessageMenu();

    requestAnimationFrame(() => {
      if (isRootMessage) {
        setEditingTarget({ kind: "chat", id: messageId, origin });
      } else {
        const chatId =
          contextChatId != null ? contextChatId : getRootChatId(messageId);

        if (chatId == null) {
          console.error("chat_id を特定できませんでした");
          showAlert(
            "編集対象の親コメント情報が取得できませんでした。もう一度お試しください。"
          );
          return;
        }

        setEditingTarget({
          kind: "reply",
          id: messageId,
          chatId,
          origin: "thread",
        });
      }

      setEditingText(found.node.text);
    });
  };

  const cancelEditMessage = () => {
    setEditingTarget(null);
    setEditingText("");
    handleCloseMessageMenu();
  };

  /* 保存：reply の時だけ chatId を必須で使う */
  const saveEditMessage = async () => {
    if (!resolvedWrId || !editingTarget) return;

    const newBody = editingText.trim();
    if (!newBody) {
      showAlert("本文が空です。");
      return;
    }

    try {
      if (editingTarget.kind === "chat") {
        await apiClient.post("/weekly-report-thread-edit", {
          id: editingTarget.id,
          wr_id: resolvedWrId,
          body: newBody,
        });

        setMessages((prev) =>
          prev.map((n) =>
            n.id === editingTarget.id ? { ...n, text: newBody } : n
          )
        );
      } else {
        const targetReplyId = editingTarget.id;
        const targetChatId = editingTarget.chatId;

        await apiClient.post("/weekly-report-thread-reply-edit", {
          id: targetReplyId,
          wr_id: resolvedWrId,
          chat_id: targetChatId,
          body: newBody,
        });

        const updateReplyDeep = (ns: ThreadNode[]): ThreadNode[] =>
          ns.map((n) => {
            if (n.id !== targetChatId) {
              return { ...n, replies: updateReplyDeep(n.replies) };
            }
            return {
              ...n,
              replies: n.replies.map((r) =>
                r.id === targetReplyId ? { ...r, text: newBody } : r
              ),
            };
          });

        setMessages((prev) => updateReplyDeep(prev));
      }
    } catch (e) {
      console.error("weekly-report-thread-edit / reply-edit error:", e);
      showAlert("編集の保存に失敗しました。");
    } finally {
      setEditingTarget(null);
      setEditingText("");
      handleCloseMessageMenu();
      lastMenuTriggerRef.current?.focus?.();
    }
  };

  /* -----------------------------------------------------
   * 削除：確認モーダル
   * --------------------------------------------------- */
  type DeleteTarget =
    | { kind: "chat"; id: number }
    | { kind: "reply"; id: number; chatId: number };

  type DeleteState = {
    open: boolean;
    target: DeleteTarget | null;
    isSubmitting: boolean;
    preview?: { user: string; text: string; ts: number };
  };

  const [deleteState, setDeleteState] = useState<DeleteState>({
    open: false,
    target: null,
    isSubmitting: false,
    preview: undefined,
  });

  /* chatId 対応版：右ペインのメニューからは chatId を渡す
   * 親コメントの場合はトップレベルのみから探す
   */
  const openDeleteConfirm = (messageId: number, chatId?: number | null) => {
    const found =
      chatId != null
        ? findNodeWithParentInChat(messages, chatId, messageId)
        : findRootNodeById(messages, messageId);

    if (!found) {
      showAlert("削除対象のメッセージが見つかりません。");
      handleCloseMessageMenu();
      return;
    }

    if (!emailEq(found.node.mail, userEmail)) {
      handleCloseMessageMenu();
      return;
    }

    const isReply = found.parent !== null;
    const rootId =
      chatId != null
        ? chatId
        : isReply
        ? getRootChatId(messageId)!
        : messageId;

    const target: DeleteTarget = isReply
      ? { kind: "reply", id: messageId, chatId: rootId }
      : { kind: "chat", id: messageId };

    setDeleteState({
      open: true,
      target,
      isSubmitting: false,
      preview: {
        user: found.node.user,
        text: found.node.text,
        ts: found.node.ts,
      },
    });

    handleCloseMessageMenu();
  };

  const closeDeleteConfirm = () => {
    setDeleteState((s) => ({
      ...s,
      open: false,
      isSubmitting: false,
    }));
  };

  const performDelete = async () => {
    if (!resolvedWrId || !deleteState.target) return;

    setDeleteState((s) => ({ ...s, isSubmitting: true }));

    try {
      if (isReplyLike(deleteState.target)) {
        const targetReplyId = deleteState.target.id;
        const targetChatId = deleteState.target.chatId;

        await apiClient.post("/weekly-report-thread-reply-delete", {
          id: targetReplyId,
          wr_id: resolvedWrId,
          chat_id: targetChatId,
        });

        setMessages((prev) =>
          prev.map((n) =>
            n.id === targetChatId
              ? {
                  ...n,
                  replies: n.replies.filter((r) => r.id !== targetReplyId),
                }
              : n
          )
        );
      } else {
        const targetChatId = deleteState.target.id;

        await apiClient.post("/weekly-report-thread-delete", {
          id: targetChatId,
          wr_id: resolvedWrId,
        });

        setMessages((prev) => prev.filter((n) => n.id !== targetChatId));
      }

      closeDeleteConfirm();
    } catch (e) {
      console.error("weekly-report-thread-delete error:", e);
      showAlert("削除に失敗しました。");
      setDeleteState((s) => ({ ...s, isSubmitting: false }));
    }
  };

  /* -----------------------------------------------------
   * 送信 UI
   * --------------------------------------------------- */
  const [topText, setTopText] = useState("");
  const [topMentions, setTopMentions] = useState<MentionUser[]>([]);
  const [topMentionAnchorEl, setTopMentionAnchorEl] =
    useState<HTMLElement | null>(null);

  const topMentionPaperRef = useRef<HTMLDivElement | null>(null);
  const composingTopRef = useRef(false);
  const [isPosting, setIsPosting] = useState(false);
  const [emptyNoticeConfirm, setEmptyNoticeConfirm] = useState<
    "top" | "thread" | null
  >(null);

  const topInputRef =
    useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);
  const threadInputRef =
    useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);

  const [threadMentions, setThreadMentions] = useState<MentionUser[]>([]);
  const [threadMentionAnchorEl, setThreadMentionAnchorEl] =
    useState<HTMLElement | null>(null);

  const threadMentionPaperRef = useRef<HTMLDivElement | null>(null);

  const reporterMention = useMemo<MentionUser | null>(() => {
    const normRaw = normStr(payload?.reporter_mail ?? "");
    if (!normRaw) return null;
    const hit = mentionCandidates.find((u) => emailEq(u.email, normRaw));
    if (hit) return hit;
    return { email: normRaw, display: normRaw, kana: "" };
  }, [mentionCandidates, payload?.reporter_mail]);

  // 返信先ポップアップに表示する履歴一覧
  const [mentionHistory, setMentionHistory] = useState<MentionUser[]>([]);
  const [mentionHistoryLoadError, setMentionHistoryLoadError] = useState(false);
  // 一度でも取得できたか（再取得の失敗時は、取得済みの履歴を残してエラー表示にしない）
  const mentionHistoryLoadedRef = useRef(false);
  // 送信成功後に履歴を再取得するためのキー
  const [mentionHistoryReloadKey, setMentionHistoryReloadKey] = useState(0);
  const reloadMentionHistory = React.useCallback(() => {
    setMentionHistoryReloadKey((k) => k + 1);
  }, []);
  useEffect(() => {
    // ログインユーザのメールアドレスが空の場合は取得処理を中止
    if (!userEmail) return;

    let abort = false;

    // 返信先に表示する履歴一覧の取得
    const loadMentionHistory = async () => {
      try {
        const res = await apiClient.get<HistoryUser[]>(
          "/get_communication_notice_history", 
          { params: { login_user: userEmail, limit: MENTION_HISTORY_LIMIT } }
        );
        
        // 一覧表示用に加工
        const history: MentionUser[] = res.data.map((u) => {
          const email = normStr(u.mail_address);
          const display =
            [normStr(u.last_name), normStr(u.first_name)]
              .filter(Boolean)
              .join(" ") || email;

          const kana =
            [normStr(u.last_name_kana), normStr(u.first_name_kana)]
              .filter(Boolean)
              .join(" ") || getKanaForName(display) || "";

          return {
            email,
            display,
            kana,
            department: normStr(u.department),
          };
        });

        if (!abort) {
          setMentionHistory(history);
          setMentionHistoryLoadError(false);
          mentionHistoryLoadedRef.current = true;
        }
      } catch (err) {
        console.warn("GET 履歴 failed:", err);
        if (!abort && !mentionHistoryLoadedRef.current) setMentionHistoryLoadError(true);
      }
    };

    void loadMentionHistory();
    return () => { abort = true; };

  }, [userEmail, mentionHistoryReloadKey]);

  // 返信先ポップアップ用：候補一覧・履歴からログインユーザー自身を除く
  // （mentionCandidates は投稿者の解決にも使うため、元の配列は絞り込まない）
  const pickerCandidates = useMemo(
    () => mentionCandidates.filter((u) => !emailEq(u.email, userEmail)),
    [mentionCandidates, userEmail]
  );
  const pickerHistory = useMemo(
    () => mentionHistory.filter((u) => !emailEq(u.email, userEmail)),
    [mentionHistory, userEmail]
  );

  // 入力項目へのフォーカス調整（送信後に返信先指定Poperを閉じた際のフォーカス処理）
  const restoreInputFocus = (input: HTMLInputElement | HTMLTextAreaElement | null) => {
    requestAnimationFrame(() => {
      const active = document.activeElement;

      // 別の入力欄やボタンにフォーカスがあれば、そちらを優先
      if (
        active &&
        active !== document.body &&
        active !== document.documentElement
      ) {
        return;
      }

      input?.focus();
    });
  };

  // 全体返信の返信先選択Popperを閉じる
  const closeTopMention = () => {
    setTopMentionAnchorEl(null);
    restoreInputFocus(topInputRef.current);
  };
  // スレッド返信の返信先Popperを閉じる
  const closeThreadMention = () => {
    setThreadMentionAnchorEl(null);
    restoreInputFocus(threadInputRef.current);
  };


  // MentionPickerPopper 用：Popper を閉じずに返信先へ追加
  const addTopMention = (mention: MentionUser) => {
    const next = { ...mention, email: normEmail(mention.email) };
    setTopMentions((prev) => {
      const exists = prev.some((m) => emailEq(m.email, next.email));
      return exists ? prev : [...prev, next];
    });
  };

  // 返信先候補のユーザ一覧をメアドで引けるようにしたMap
  const candidatesByEmail = useMemo(
    () => new Map(mentionCandidates.map((u) => [normEmail(u.email), u])),
    [mentionCandidates]
  );

  // 既定の返信先を組み立てる（全体返信・スレッド返信で共通）
  // - メールアドレスでない値と重複は除く（先に出てきたものを優先）
  // - ユーザー一覧が取れているときは、USER_INFO_MANAGE に存在しないユーザー（削除済みなど）を除く
  // - ログインユーザー自身は除く
  const buildDefaultMentions = React.useCallback(
    (sources: MentionSourceUser[]): MentionUser[] => {

      const defaults = new Map<string, MentionUser>();
      const selfEmail = normEmail(userEmail);

      for (const { mail, user } of sources) {
        const email = normEmail(mail);
        if (!email || !email.includes("@")) continue;
        if (selfEmail && email === selfEmail) continue;
        if (defaults.has(email)) continue;

        const hit = candidatesByEmail.get(email);  // メアドで候補者リストから情報を取得
        if (!hit && candidatesByEmail.size > 0) continue;  // 候補者リストあり・ヒットしなかった場合はデフォルトに設定しない
        defaults.set(
          email,
          hit ? { ...hit, email } : { email, display: user || email, kana: "" }
        );
      }
      return [...defaults.values()];
    },
    [candidatesByEmail, userEmail]
  );

  // 全体返信のデフォルト通知先（週報発行者）を取得
  //  発行者が未解決・メールアドレスでない場合は null
  const buildTopDefaultMentions = React.useCallback((): MentionUser[] | null => {
    if (!reporterMention) return null;
    if (!reporterMention.email.includes("@")) return null;

    return buildDefaultMentions([
      { mail: reporterMention.email, user: reporterMention.display },
    ]);
  }, [reporterMention, buildDefaultMentions]);

  // 初期表示：週報発行者を返信先(topMentions)にセット（1回だけ）
  const topMentionsInitializedRef = useRef(false);
  useEffect(() => {

    // 発行者・ユーザー一覧の取得を待つ
    if (topMentionsInitializedRef.current) return;
    if (!userEmail) return;  // 自分を除外するためログインユーザーの取得を待つ
    if (!reporterMention) return;
    if (mentionCandidates.length === 0) return;
    if (!reporterMention.email.includes("@")) return;

    // デフォルトの通知先を設定
    const defaults = buildTopDefaultMentions();
    if (!defaults) return;

    topMentionsInitializedRef.current = true;

    // 読み込み前に手動追加された返信先も維持
    setTopMentions((prev) => {
      const merged = new Map(
        prev.map((u) => [normEmail(u.email), u])
      );

      for (const user of defaults) {
        if (!merged.has(user.email)) merged.set(user.email, user);
      }

      return [...merged.values()];
    });

  }, [buildDefaultMentions, reporterMention, mentionCandidates, userEmail, buildTopDefaultMentions]);

  // MentionPickerPopper 用：Popper を閉じずに返信先へ追加
  const addThreadMention = (mention: MentionUser) => {
    const next = { ...mention, email: normEmail(mention.email) };
    setThreadMentions((prev) => {
      const exists = prev.some((m) => emailEq(m.email, next.email));
      return exists ? prev : [...prev, next];
    });
  };

  useEffect(() => {
    if (!topMentionAnchorEl && !threadMentionAnchorEl) return;

    const handleClick = (event: MouseEvent) => {
      const target = event.target as Node | null;
      const topPaper = topMentionPaperRef.current;
      const threadPaper = threadMentionPaperRef.current;
      const topAnchor = topMentionAnchorEl;
      const threadAnchor = threadMentionAnchorEl;

      if (topPaper && topPaper.contains(target)) return;
      if (threadPaper && threadPaper.contains(target)) return;
      if (topAnchor && topAnchor.contains(target)) return;
      if (threadAnchor && threadAnchor.contains(target)) return;
      // 部署Selectのリストは Portal で body 直下に出るため、DOM 上は Paper の外になる。閉じないよう除外
      if (target instanceof Element && target.closest(".MuiPopover-root")) return;


      if (topMentionAnchorEl) {
        closeTopMention();
      }
      if (threadMentionAnchorEl) {
        closeThreadMention();
      }
    };

    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    topMentionAnchorEl, 
    threadMentionAnchorEl,
  ]);

  const submitTop = async (options?: { skipConfirm?: boolean }) => {
    const text = topText.trim();
    if (!text) return;
    if (!resolvedWrId) {
      showAlert("wr_id が取得できていません。URL の wr_id を確認してください。");
      return;
    }
    if (!userEmail) {
      showAlert("サインイン情報（メール）の取得中です。数秒後にお試しください。");
      return;
    }
    if (isPosting) return;

    const mentionMails = topMentions.map((m) => m.email);
    // 返信先が上限を超えている場合は送信しない（本文・返信先は保持）
    if (mentionMails.length > MAX_MENTIONS) {
      showAlert(`返信先は${MAX_MENTIONS}件までです（現在 ${mentionMails.length} 件）。返信先を減らしてから送信してください。`);
      return;
    }
    if (!options?.skipConfirm && mentionMails.length === 0) {
      setEmptyNoticeConfirm("top");
      return;
    }

    setIsPosting(true);
    try {
      const payloadBody = {
        wr_id: resolvedWrId,
        mail_address: userEmail,
        last_name: userLastName || undefined,
        first_name: userFirstName || undefined,
        body: text,
        thread_url: openedUrlRef.current || undefined,
        ...(mentionMails.length > 0
          ? { mention_mails: mentionMails }
          : {}),
      };
      const res = await apiClient.post<WeeklyReportThreadOut>(
        "/weekly-report-thread-post",
        payloadBody
      );
      const data = res.data;

      const displayNameFromApi =
        data.last_name || data.first_name
          ? `${data.last_name ?? ""} ${data.first_name ?? ""}`.trim()
          : data.mail_address;

      const ts =
        data.created_at ? new Date(data.created_at).getTime() : Date.now();

      const node = addTopMessage(
        displayNameFromApi,
        data.body,
        ts,
        data.id,
        data.mail_address,
        data.notice_users
      );
      setTopText("");
      // 返信先を指定して送信した場合は、今回の返信先を含めて履歴を再取得
      if (mentionMails.length > 0) reloadMentionHistory();

      // 送信成功後、デフォルトの通知先（週報発行者）を再セット
      const defaults = buildTopDefaultMentions();
      setTopMentions(defaults ?? []);

      setTopMentionAnchorEl(null);
      requestAnimationFrame(() => topInputRef.current?.focus());

      // スレッド返信を入力中の場合は、自動で開かない
      if ((threadReplyText.trim() === "") && (editingTarget?.origin !== "thread")){
        // 送信したメッセージのスレッドを開く（スレッドでの会話を促すため）
        openThread(node.id, node);
      }

    } catch (e) {
      console.error("weekly-report-thread-post error:", e);
      showAlert("コメントの投稿に失敗しました。");

    } finally {
      setIsPosting(false);
    }
  };

  const onTopKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (composingTopRef.current) return;
    if (e.key === "Enter" && e.shiftKey) {
      e.preventDefault();
      void submitTop();
    }
  };

  /* -----------------------------------------------------
   * スレッド（右サイド）
   * --------------------------------------------------- */
  const [isThreadOpen, setIsThreadOpen] = useState(false);
  const [activeThreadRootId, setActiveThreadRootId] =
    useState<number | null>(null);

  const [threadReplyText, setThreadReplyText] = useState("");
  const composingThreadRef = useRef(false);
  const [isReplyPosting, setIsReplyPosting] = useState(false);

  const submitThreadReply = async (options?: { skipConfirm?: boolean }) => {
    if (!activeThreadRootId) return;
    const text = threadReplyText.trim();
    if (!text) return;
    if (!resolvedWrId) {
      showAlert("wr_id が取得できていません。URL の wr_id を確認してください。");
      return;
    }
    if (!userEmail) {
      showAlert("サインイン情報（メール）の取得中です。数秒後にお試しください。");
      return;
    }
    if (isReplyPosting) return;

    const mentionMails = threadMentions.map((m) => m.email);
    // 返信先が上限を超えている場合は送信しない（本文・返信先は保持）
    if (mentionMails.length > MAX_MENTIONS) {
      showAlert(`返信先は${MAX_MENTIONS}件までです（現在 ${mentionMails.length} 件）。返信先を減らしてから送信してください。`);
      return;
    }
    if (!options?.skipConfirm && mentionMails.length === 0) {
      setEmptyNoticeConfirm("thread");
      return;
    }

    setIsReplyPosting(true);
    try {
      const payloadBody = {
        wr_id: resolvedWrId,
        chat_id: activeThreadRootId,
        mail_address: userEmail,
        last_name: userLastName || undefined,
        first_name: userFirstName || undefined,
        body: text,
        thread_url: openedUrlRef.current || undefined,
        ...(mentionMails.length > 0
          ? { mention_mails: mentionMails }
          : {}),
      };
      const res = await apiClient.post<WeeklyReportThreadReplyOut>(
        "/weekly-report-thread-reply-post",
        payloadBody
      );
      const data = res.data;

      const displayNameFromApi =
        data.last_name || data.first_name
          ? `${data.last_name ?? ""} ${data.first_name ?? ""}`.trim()
          : data.mail_address;

      const ts =
        data.created_at ? new Date(data.created_at).getTime() : Date.now();

      addReply(
        activeThreadRootId,
        displayNameFromApi,
        data.body,
        ts,
        data.id,
        data.mail_address,
        data.notice_users
      );
      setThreadReplyText("");
      setThreadMentions([]);
      // 返信先を指定して送信した場合は、今回の返信先を含めて履歴を再取得
      if (mentionMails.length > 0) reloadMentionHistory();
      // 送信成功後、今回の返信を最新返信としてデフォルト通知先を再計算
      const root = messages.find((m) => m.id === activeThreadRootId);
      setThreadMentions(
        buildThreadDefaultMentions(root, {
          mail: data.mail_address,
          user: displayNameFromApi,
          noticeUsers: data.notice_users ?? [],
        })
      );
      setThreadMentionAnchorEl(null);
    } catch (e) {
      console.error("weekly-report-thread-reply-post error:", e);
      showAlert("返信の投稿に失敗しました。");
    } finally {
      setIsReplyPosting(false);
    }
  };

  const onThreadKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (composingThreadRef.current) return;
    if (e.key === "Enter" && e.shiftKey) {
      e.preventDefault();
      void submitThreadReply();
    }
  };

  /* -----------------------------------------------------
   * スレッド表示用メッセージ
   * --------------------------------------------------- */
  const activeThreadMessages = useMemo(() => {
    if (!activeThreadRootId)
      return [] as { node: ThreadNode; depth: number }[];

    const root = messages.find((m) => m.id === activeThreadRootId);
    if (!root) return [];

    const result: { node: ThreadNode; depth: number }[] = [];

    // ルートメッセージ
    result.push({ node: root, depth: 0 });

    const targetChatId = activeThreadRootId;

    const walkReplies = (ns: ThreadNode[], depth: number) => {
      (ns || []).forEach((child) => {
        const belongsChatId = child.chatId ?? targetChatId;
        if (belongsChatId === targetChatId) {
          result.push({ node: child, depth });
        }
        if (child.replies && child.replies.length > 0) {
          walkReplies(child.replies, depth + 1);
        }
      });
    };

    walkReplies(root.replies, 1);

    const [rootItem, ...others] = result;
    const sortedReplies = others.sort(
      (a, b) => a.node.ts - b.node.ts
    );

    return [rootItem, ...sortedReplies];
  }, [activeThreadRootId, messages]);

  const activeThreadRoot = useMemo(
    () =>
      activeThreadRootId
        ? messages.find((m) => m.id === activeThreadRootId) ?? null
        : null,
    [activeThreadRootId, messages]
  );

  /* -----------------------------------------------------
   * 描画関連
   * --------------------------------------------------- */
  const headerTitle = useMemo(() => {
    const title = (payload?.title ?? "").trim();
    return title ? `週報 ${title}のスレッド` : "";
  }, [payload]);

  useEffect(() => {
    document.title = "週報アプリ";
  }, []);


  /* -----------------------------------------------------
   * スレッドパネル用リサイズ
   * --------------------------------------------------- */
  const [threadWidth, setThreadWidth] = useState<number>(DEFAULT_THREAD_WIDTH);
  const [isResizing, setIsResizing] = useState(false);
  const layoutRef = useRef<HTMLDivElement | null>(null);
  const hasOpenedThreadRef = useRef(false);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing || !layoutRef.current) return;

      const rect = layoutRef.current.getBoundingClientRect();
      const totalWidth = rect.width;
      const relativeX = e.clientX - rect.left;

      // 右ペイン幅を計算
      let newThreadWidth = totalWidth - relativeX;

      const VERY_SMALL_MAX = 500; // ～500px はモバイル扱い
      const MIN_MAIN_WIDTH = 260;
      const MIN_THREAD_WIDTH = 220;
      const MIN_RATIO = 0.3;
      const MAX_RATIO = 0.7;

      if (totalWidth <= VERY_SMALL_MAX) {
        // ～500px：左右の比率だけを見る（30%〜70%）
        const minThread = totalWidth * MIN_RATIO;
        const maxThread = totalWidth * MAX_RATIO;

        if (newThreadWidth < minThread) newThreadWidth = minThread;
        if (newThreadWidth > maxThread) newThreadWidth = maxThread;
      } else {
        // 500px 超：左ペイン 260px 以上 & 右ペインは最大 70%
        const maxByMain = Math.max(
          MIN_THREAD_WIDTH,
          totalWidth - MIN_MAIN_WIDTH
        );
        const maxByRatio = totalWidth * MAX_RATIO;
        const maxThread = Math.min(maxByMain, maxByRatio);

        if (newThreadWidth < MIN_THREAD_WIDTH) {
          newThreadWidth = MIN_THREAD_WIDTH;
        }
        if (newThreadWidth > maxThread) {
          newThreadWidth = maxThread;
        }
      }

      setThreadWidth(newThreadWidth);
    };

    const handleMouseUp = () => {
      if (isResizing) setIsResizing(false);
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isResizing]);


  // スレッドのデフォルト通知先：最初の投稿者 + 最新返信の投稿者 + 最新返信の通知先
  const buildThreadDefaultMentions = (
    root?: ThreadNode,
    latestReply: MentionSource | undefined = root?.replies
      .slice()
      .sort((a, b) => b.ts - a.ts || b.id - a.id)[0] ?? root    // 返信がなければ親コメントを最新として扱う
  ): MentionUser[] =>
    buildDefaultMentions([
      { mail: root?.mail, user: root?.user },
      { mail: latestReply?.mail, user: latestReply?.user },
      ...(latestReply?.noticeUsers ?? []).map((notice) => ({  // 通知先をメンションに追加する
        mail: notice.mail_address,
        user: notice.user,
      })),
    ]);

  // rootNode：送信直後など messages への反映前に開く場合に渡す
  const openThread = (rootId: number, rootNode?: ThreadNode) => {

    // 既に右側で開いているスレッドか（state 更新前の値で判定する）
    const isSameThread = isThreadOpen && activeThreadRootId === rootId;

    setActiveThreadRootId(rootId);
    setIsThreadOpen(true);

    // 同じスレッドの「返信」を押した場合は、既存の通知先を維持する
    if(!isSameThread){
      // デフォルトの通知先をセット
      const root = rootNode ?? messages.find((m) => m.id === rootId);
      setThreadMentions(buildThreadDefaultMentions(root));
    }

    if (!layoutRef.current) {
      setThreadWidth(DEFAULT_THREAD_WIDTH);
      return;
    }

    const rect = layoutRef.current.getBoundingClientRect();
    const totalWidth = rect.width;

    const isVerySmall = totalWidth <= 500; // ～500px
    const isSmall = totalWidth > 500 && totalWidth <= 600; // 500〜600px
    // 600〜960px はデフォルト 480px

    const MAX_RATIO = 0.7;
    const MIN_RATIO = 0.3;
    const MIN_MAIN_WIDTH = 260;
    const MIN_THREAD_WIDTH = 220;

    const calcMaxThreadWidth = (baseWidth: number) => {
      const maxByRatio = baseWidth * MAX_RATIO;

      // 画面がかなり狭いときはメイン最小幅は考えず比率だけで制限
      if (baseWidth <= MIN_MAIN_WIDTH + MIN_THREAD_WIDTH) {
        return maxByRatio;
      }

      const maxByMain = Math.max(
        MIN_THREAD_WIDTH,
        baseWidth - MIN_MAIN_WIDTH
      );
      // 「左260px以上」と「最大70%」の両方を満たす範囲
      return Math.min(maxByRatio, maxByMain);
    };

    if (!hasOpenedThreadRef.current) {
      // ★ 初回オープン
      hasOpenedThreadRef.current = true;

      let initialWidth: number;
      if (isVerySmall) {
        // ～500px：初回は 220px
        initialWidth = 220;
      } else if (isSmall) {
        // 500〜600px：初回は 240px
        initialWidth = 240;
      } else if (totalWidth <= 960) {
        // 600〜960px：初回は 560px
        initialWidth = DEFAULT_THREAD_WIDTH;
      } else {
        // 960px より広い場合もひとまず 560px ベース
        initialWidth = DEFAULT_THREAD_WIDTH;
      }

      const maxThread = calcMaxThreadWidth(totalWidth);
      const minThread = isVerySmall
        ? totalWidth * MIN_RATIO
        : MIN_THREAD_WIDTH;

      let clamped = initialWidth;
      if (clamped < minThread) clamped = minThread;
      if (clamped > maxThread) clamped = maxThread;

      setThreadWidth(clamped);
    } else {
      // ★ 2回目以降・他カードの返信を開いたとき
      const maxThread = calcMaxThreadWidth(totalWidth);
      const minThread = isVerySmall
        ? totalWidth * MIN_RATIO
        : MIN_THREAD_WIDTH;

      const fallbackInitial = isVerySmall
        ? 220
        : isSmall
        ? 240
        : DEFAULT_THREAD_WIDTH;

      let current = threadWidth || fallbackInitial;

      if (current < minThread) current = minThread;
      if (current > maxThread) current = maxThread;

      setThreadWidth(current);
    }
  };

  const closeThread = () => {
    setIsThreadOpen(false);
    setActiveThreadRootId(null);
    setThreadReplyText("");
    setThreadMentions([]);
    setThreadMentionAnchorEl(null);
  };

  /* -----------------------------------------------------
   * 初期表示時にスレッドを右ペインに開く（スレッドでの返信を促すため）
   *  - URL に chat_id あり（メールのリンク）：そのスレッド
   *  - chat_id なし／該当なし（週報連から等）：最後に投稿・返信があったスレッド
   * --------------------------------------------------- */
  const initialThreadOpenedRef = useRef(false);
  // 自動オープンしたスレッドの親コメント（メイン側で見える位置までスクロールするため）
  const autoOpenScrollIdRef = useRef<number | null>(null);
  useEffect(() => {

    // 1回自動オープンを行ったら以降は行わない
    if (initialThreadOpenedRef.current) return;

    if (initialFetchStatus === "failed" || isThreadOpen) {
      // 初回取得が失敗した場合とユーザが手動でスレッドを開いた場合は自動オープンしない
      initialThreadOpenedRef.current = true;
      return;
    }
    if (initialFetchStatus !== "success") return;
    if (!userEmail || userInfoLoading) return;
    initialThreadOpenedRef.current = true;
    if (messages.length === 0) return;

    // 最近のコメント投稿を取得
    const lastActivity = (m: ThreadNode) =>
      Math.max(m.ts, ...m.replies.map((r) => r.ts));

    const target =
      messages.find((m) => String(m.id) === initialChatId) ??
      messages.reduce((a, b) => (lastActivity(b) > lastActivity(a) ? b : a));
    openThread(target.id, target);
    autoOpenScrollIdRef.current = target.id;

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialFetchStatus, isThreadOpen, userEmail, userInfoLoading]);

  /* -----------------------------------------------------
   * スクロール（メイン）
   * --------------------------------------------------- */
  const threadScrollRef = useRef<HTMLDivElement | null>(null);
  const mainScrollRef = useRef<HTMLDivElement | null>(null);

  const scrollMainToBottom = () => {
    if (!mainScrollRef.current) return;
    const el = mainScrollRef.current;
    el.scrollTop = el.scrollHeight;
  };

  useEffect(() => {
    scrollMainToBottom();
    try {
      window.scrollTo({
        top: document.documentElement.scrollHeight,
        behavior: "auto",
      });
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    if (!messages.length) return;
    scrollMainToBottom();
  }, [messages.length]);

  // 自動オープンしたスレッドの親コメントが見える位置までスクロール
  //  ※ 上の最下部スクロールと右ペイン表示によるレイアウト変更の後に行う
  useEffect(() => {
    const targetId = autoOpenScrollIdRef.current;
    if (targetId == null || !isThreadOpen || activeThreadRootId !== targetId) return;
    autoOpenScrollIdRef.current = null;
    requestAnimationFrame(() => {
      mainScrollRef.current
        ?.querySelector(`[data-chat-id="${targetId}"]`)
        ?.scrollIntoView({ block: "nearest" });
    });
  }, [isThreadOpen, activeThreadRootId]);

  /* -----------------------------------------------------
   * スレッドメッセージエリア 自動スクロール
   * --------------------------------------------------- */
  useEffect(() => {
    if (!isThreadOpen) return;
    if (!threadScrollRef.current) return;
    threadScrollRef.current.scrollTop = threadScrollRef.current.scrollHeight;
  }, [isThreadOpen, activeThreadMessages.length]);

  /* -----------------------------------------------------
   * JSX
   * --------------------------------------------------- */
  return (
    <Box
      ref={rootRef}
      sx={{
        height:
          containerHeight != null ? `${containerHeight}px` : "100vh",
        display: "flex",
        flexDirection: "column",
        bgcolor: isDark ? "#0b1120" : "#f5f5f7",
      }}
    >
      <Box
        ref={layoutRef}
        sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "row",
          overflow: "hidden",
        }}
      >
        {/* 左：メイン */}
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            ref={mainScrollRef}
            sx={{ flex: 1, minHeight: 0, overflow: "auto" }}
          >
            <Container maxWidth="lg">
              {/* 週報メタ */}
              <Card
                className="weekly-payload-card"
                variant="outlined"
                sx={{
                  mb: 2,
                  borderRadius: 3,
                  overflow: "hidden",
                  borderColor: "divider",
                  boxShadow: isDark
                    ? "0 4px 12px rgba(0, 0, 0, .45)"
                    : "0 4px 12px rgba(0, 0, 0, .06)",
                  position: "relative",
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    height: 3,
                    bgcolor: "primary.main",
                  },
                }}
              >
                <CardContent sx={{ pt: 2, pb: 2.25 }}>
                  {(payload?.title || true) && (
                    <Stack
                      direction="row"
                      alignItems="baseline"
                      spacing={2}
                      sx={{ mb: payload?.body ? 0.5 : 0 }}
                    >
                      {payload?.title && (
                        <Typography
                          variant="h6"
                          sx={{
                            fontFamily: "monospace",
                            fontWeight: 700,
                            letterSpacing: 0,
                            lineHeight: 1.25,
                            fontSize: { xs: 20, md: 22.5 },
                            color: isDark ? "#38bdf8" : "rgb(39, 65, 211)",
                            textDecorationLine: "underline",
                            textDecorationColor: "currentColor",
                            textUnderlineOffset: "2px",
                            textDecorationThickness: "1px",
                            flexGrow: 1,
                            minWidth: 0,
                            whiteSpace: "pre-wrap",
                            overflowWrap: "anywhere",
                            wordBreak: "break-word",
                          }}
                        >
                          {payload.title}
                        </Typography>
                      )}
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          whiteSpace: "nowrap",
                          ml: "auto",
                          fontSize: { xs: 11, sm: 12 },
                        }}
                      >
                      </Typography>
                    </Stack>
                  )}

                  {payload?.body && (
                    <>
                      <Divider sx={{ my: 1 }} />
                      <Typography
                        variant="body1"
                        sx={{
                          fontFamily: "monospace",
                          color: isDark ? "#e2e8f0" : "rgb(24, 5, 0)",
                          whiteSpace: "pre-wrap",
                          overflowWrap: "anywhere",
                          wordBreak: "break-word",
                          lineHeight: 1.25,
                          fontSize: { xs: 20, md: 22.2 },
                        }}
                      >
                        {payload.body}
                      </Typography>
                    </>
                  )}
                </CardContent>
              </Card>

              {/* メインスレッド本体（親コメント） */}
              {messages.map((m) => (
                <MessageCard
                  key={m.id}
                  node={m}
                  currentUserEmail={userEmail}
                  isActive={isThreadOpen && activeThreadRootId === m.id}
                  onOpenThread={openThread}
                  onOpenMenu={handleOpenMessageMenu}
                  isEditing={
                    editingTarget?.kind === "chat" &&
                    editingTarget.id === m.id &&
                    editingTarget.origin === "main"
                  }
                  editingText={
                    editingTarget?.kind === "chat" &&
                    editingTarget.id === m.id &&
                    editingTarget.origin === "main"
                      ? editingText
                      : ""
                  }
                  onChangeEditingText={setEditingText}
                  onSaveEdit={saveEditMessage}
                  onCancelEdit={cancelEditMessage}
                />
              ))}
            </Container>
          </Box>

          {/* 下部：新規メッセージ入力 */}
          <Box
            sx={{
              borderTop: 1,
              borderColor: "divider",
              backgroundColor: "background.paper",
              py: 1.5,
            }}
          >
            <Container maxWidth="lg">
              {userInfoLoadError && (
                <Alert
                  severity="warning"
                  sx={{ mb: 0.75, py: 0, fontSize: 12, alignItems: "center", "& .MuiAlert-action": { pt: 0 } }}
                  action={
                    <Button
                      color="warning"
                      variant="outlined"
                      size="small"
                      startIcon={
                        userInfoLoading
                          ? <CircularProgress size={14} color="inherit" />
                          : <RefreshIcon />
                      }
                      disabled={userInfoLoading}   // 読み込み中は押せないようにする
                      onClick={reloadAllUsers}
                      sx={{ whiteSpace: "nowrap", bgcolor: "background.paper" }}
                    >
                      再読み込み
                    </Button>
                  }
                >
                  ユーザー一覧の取得に失敗しました。返信先の自動設定や新規返信先の選択ができない場合があります。
                </Alert>
              )}
              <Box
                sx={{
                  mb: 0.75,
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 0.5,
                  alignItems: "center",
                }}
              >
                <Button
                  size="small"
                  variant="outlined"
                  onClick={(e) => setTopMentionAnchorEl(e.currentTarget)}
                  sx={{ fontSize: 12, height: 28 }}
                >
                  返信先
                </Button>
                <NoticeStatusHelpLink />
                {topMentions.map((m) => (
                  <Tooltip key={m.email} title={m.email}>
                  <Chip
                    label={m.display}
                    size="small"
                    onDelete={() => {
                      setTopMentions((prev) =>
                        prev.filter((x) => !emailEq(x.email, m.email))
                      );
                    }}
                  />
                </Tooltip>
                ))}
                {userInfoLoading && <MentionLoadingIndicator />}
                <MentionPickerPopper
                  anchorEl={topMentionAnchorEl}
                  paperRef={topMentionPaperRef}
                  onClose={closeTopMention}
                  mentions={topMentions}
                  onRemoveMention={(u) =>
                    setTopMentions((prev) =>
                      prev.filter((x) => !emailEq(x.email, u.email))
                    )
                  }
                  history={pickerHistory}
                  historyLoadError={mentionHistoryLoadError}
                  mentionCandidates={pickerCandidates}
                  candidatesLoading={userInfoLoading}
                  candidatesLoadError={userInfoLoadError}
                  onSelectMention={addTopMention}
                />
              </Box>
              {topMentions.length > MAX_MENTIONS && (
                <Alert severity="warning" sx={{ mb: 0.75, py: 0, fontSize: 12 }}>
                  返信先が{MAX_MENTIONS}件を超えています（現在 {topMentions.length} 件）。{MAX_MENTIONS}件以下にしてから送信してください。
                </Alert>
              )}
              <Stack direction="row" spacing={1} alignItems="flex-end">
                <Box sx={{ flex: 1, position: "relative" }}>
                  <TextField
                    id="newMessageInput"
                    name="newMessage"
                    inputRef={topInputRef}
                    fullWidth
                    multiline
                    minRows={2}
                    placeholder="新規メッセージを入力…"
                    value={topText}
                  onChange={(e) => {
                    const next = e.target.value;
                    setTopText(next);
                    if (topInputRef.current !== document.activeElement) {
                      topInputRef.current?.focus();
                    }
                  }}
                    onKeyDown={onTopKeyDown}
                    onCompositionStart={() =>
                      (composingTopRef.current = true)
                    }
                    onCompositionEnd={() =>
                      (composingTopRef.current = false)
                    }
                    sx={{
                      "& .MuiInputBase-input::placeholder": { fontSize: 16 },
                      "& .MuiInputBase-input": { fontSize: 14 },
                    }}
                  />
                </Box>
                <Button
                  variant="contained"
                  onClick={() => void submitTop()}
                  disabled={!topText.trim() || isPosting}
                  sx={{ fontSize: 13 }}
                >
                  送信
                </Button>
              </Stack>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 0.5, display: "block" }}
              >
                投稿後は一番下に追加されます（Enterで改行 / Shift+Enterで送信）
              </Typography>
            </Container>
          </Box>
        </Box>

        {/* 仕切り（ドラッグで右ペインの幅変更） */}
        {isThreadOpen && (
          <Box
            onMouseDown={(e) => {
              e.preventDefault();
              setIsResizing(true);
            }}
            sx={{
              width: 4,
              cursor: "col-resize",
              bgcolor: "divider",
              "&:hover": { bgcolor: "text.disabled" },
              flexShrink: 0,
            }}
          />
        )}

        {/* 右：スレッドペイン */}
        {isThreadOpen && activeThreadRoot && (
          <Box
            sx={{
              width: threadWidth,
              minWidth: 260,
              flexShrink: 0,
              borderLeft: 1,
              borderColor: "divider",
              display: "flex",
              flexDirection: "column",
              bgcolor: "background.paper",
            }}
          >
            <Box
              sx={{
                height: 80,
                px: 1.5,
                borderBottom: 1,
                borderColor: "divider",
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 700,
                  flexGrow: 1,
                  fontSize: 16,
                  lineHeight: 1,
                }}
                noWrap
                title={activeThreadRoot.text}
              >
                スレッド
              </Typography>
              <IconButton
                onClick={closeThread}
                aria-label="スレッドを閉じる"
                sx={{ width: 36, height: 36, ml: 0.5 }}
              >
                <CloseIcon fontSize="medium" />
              </IconButton>
            </Box>

            <Box
              ref={threadScrollRef}
              sx={{ flex: 1, minHeight: 0, overflow: "auto", px: 1.5, py: 1 }}
            >
              {activeThreadMessages.map(({ node, depth }) => {
                const isRoot = depth === 0;

                const isEditingThis =
                  (isRoot &&
                    editingTarget?.kind === "chat" &&
                    editingTarget.id === node.id &&
                    editingTarget.origin === "thread") ||
                  (!isRoot &&
                    editingTarget?.kind === "reply" &&
                    editingTarget.id === node.id);

                const canEdit = emailEq(node.mail, userEmail);

                const itemKey = isRoot
                  ? `chat-${node.id}`
                  : `reply-${node.id}`;

                const menuChatId = isRoot ? null : activeThreadRootId;

                return (
                  <Box key={itemKey} sx={{ mb: 1 }}>
                    <Card
                      variant="outlined"
                      sx={{
                        borderRadius: 2,
                        position: "relative",
                        "&:hover .message-menu-button": {
                          opacity: canEdit ? 1 : 0,
                        },
                      }}
                    >
                      <CardContent
                        sx={{
                          pb: 1.5,
                          pl:
                            depth > 0
                              ? Math.min(depth, 3) * 2
                              : undefined,
                        }}
                      >
                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing={1}
                          sx={{ mb: 0.5 }}
                        >
                          <Avatar sx={{ width: 24, height: 24 }}>
                            {displayUserInitial(node.user)}
                          </Avatar>
                          <Typography
                            fontWeight={700}
                            variant="body2"
                            sx={{ fontSize: 13 }}
                          >
                            {node.user}
                          </Typography>
                          <NoticeUsersInline
                            noticeUsers={node.noticeUsers}
                          />
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ ml: "auto" }}
                          >
                            {fmt(node.ts)}
                          </Typography>
                        </Stack>

        {isEditingThis ? (
          <>
            <TextField
              id="editReplyMessage"
              name="editReplyMessage"
              fullWidth
                              multiline
                              minRows={2}
                              value={editingText}
                              onChange={(e) =>
                                setEditingText(e.target.value)
                              }
                              sx={{ mt: 0.5 }}
                            />
                            <Stack
                              direction="row"
                              spacing={1}
                              sx={{ mt: 1, justifyContent: "flex-end" }}
                            >
                              <Button
                                size="small"
                                variant="contained"
                                color="success"
                                onClick={saveEditMessage}
                              >
                                保存
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={cancelEditMessage}
                              >
                                キャンセル
                              </Button>
            </Stack>
          </>
        ) : (
          <Box
            sx={{
              mt: 0.5,
              display: "flex",
              alignItems: "flex-start",
            }}
          >
            <Typography
              variant="body2"
              sx={{
                whiteSpace: "pre-wrap",
                overflowWrap: "anywhere",
                wordBreak: "break-word",
                lineHeight: 1.7,
                fontSize: 16,
                flexGrow: 1,
                minWidth: 0,
              }}
            >
              {node.text}
            </Typography>

            {canEdit && (
              <IconButton
                size="small"
                className="message-menu-button"
                onClick={(e) =>
                  handleOpenMessageMenu(
                    e,
                    node.id,
                    menuChatId
                  )
                }
                sx={{
                  opacity: 0,
                  transition: "opacity 0.2s",
                }}
                aria-label="メニューを開く"
              >
                <MoreVertIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        )}

      </CardContent>
    </Card>
  </Box>
);
              })}
            </Box>

            <Box
              sx={{
                borderTop: 1,
                borderColor: "divider",
                bgcolor: isDark ? "#0b1120" : "#f5f5f7",
                pt: 1.25,
                pb: 4.58,
                px: 1.5,
              }}
            >
              <Box
                sx={{
                  mb: 0.75,
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 0.5,
                  alignItems: "center",
                }}
              >
                <Button
                  size="small"
                  variant="outlined"
                  onClick={(e) =>
                    setThreadMentionAnchorEl(e.currentTarget)
                  }
                  sx={{ fontSize: 12, height: 28 }}
                >
                  返信先
                </Button>
                <NoticeStatusHelpLink />
                {threadMentions.map((m) => (
                  <Tooltip key={m.email} title={m.email}>
                    <Chip
                      label={m.display}
                      size="small"
                      onDelete={() => {
                        setThreadMentions((prev) =>
                          prev.filter((x) => !emailEq(x.email, m.email))
                        );
                      }}
                    />
                  </Tooltip>
                ))}
                {userInfoLoading && <MentionLoadingIndicator />}

                <MentionPickerPopper
                  anchorEl={threadMentionAnchorEl}
                  paperRef={threadMentionPaperRef}
                  onClose={closeThreadMention}
                  mentions={threadMentions}
                  onRemoveMention={(u) =>
                    setThreadMentions((prev) =>
                      prev.filter((x) => !emailEq(x.email, u.email))
                    )
                  }
                  history={pickerHistory}
                  historyLoadError={mentionHistoryLoadError}
                  mentionCandidates={pickerCandidates}
                  candidatesLoading={userInfoLoading}
                  candidatesLoadError={userInfoLoadError}
                  onSelectMention={addThreadMention}
                />
              </Box>
              {threadMentions.length > MAX_MENTIONS && (
                <Alert severity="warning" sx={{ mb: 0.75, py: 0, fontSize: 12 }}>
                  返信先が{MAX_MENTIONS}件を超えています（現在 {threadMentions.length} 件）。{MAX_MENTIONS}件以下にしてから送信してください。
                </Alert>
              )}
              <Stack direction="row" spacing={1} alignItems="flex-end">
                <Box sx={{ flex: 1, position: "relative" }}>
                  <TextField
                    id="threadReplyInput"
                    name="threadReply"
                    inputRef={threadInputRef}
                    fullWidth
                    multiline
                    minRows={2}
                    placeholder="このスレッドに返信…"
                    value={threadReplyText}
                    onChange={(e) => {
                      const next = e.target.value;
                      setThreadReplyText(next);
                    }}
                    onKeyDown={onThreadKeyDown}
                    onCompositionStart={() =>
                      (composingThreadRef.current = true)
                    }
                    onCompositionEnd={() =>
                      (composingThreadRef.current = false)
                    }
                    sx={{
                      "& .MuiInputBase-input::placeholder": { fontSize: 16 },
                      "& .MuiInputBase-input": { fontSize: 12 },
                    }}
                  />
                </Box>
                <Button
                  variant="contained"
                  onClick={() => void submitThreadReply()}
                  disabled={!threadReplyText.trim() || isReplyPosting}
                  sx={{ fontSize: 13 }}
                >
                  送信
                </Button>
              </Stack>
            </Box>
          </Box>
        )}
      </Box>

      {/* 三点メニュー（非モーダル Popper 版） */}
      <Popper
        open={isMenuOpen}
        anchorEl={menuAnchorEl}
        placement="bottom-end"
        disablePortal
        modifiers={[{ name: "offset", options: { offset: [0, 4] } }]}
      >
        <Paper elevation={8} sx={{ mt: 0.5 }}>
          <ClickAwayListener onClickAway={handleCloseMessageMenu}>
            <MenuList
              ref={menuListRef}
              autoFocusItem={false}
              disableListWrap
              sx={{ py: 0.5 }}
            >
              <MenuItem
                disableRipple
                onClick={() => {
                  if (menuTarget != null) {
                    startEditMessage(menuTarget.id);
                  }
                }}
                sx={{ color: "green" }}
              >
                編集
              </MenuItem>
              <MenuItem
                disableRipple
                onClick={() => {
                  if (menuTarget != null) {
                    openDeleteConfirm(menuTarget.id, menuTarget.chatId);
                  }
                }}
                sx={{ color: "error.main" }}
              >
                削除
              </MenuItem>
            </MenuList>
          </ClickAwayListener>
        </Paper>
      </Popper>

      <AlertDialog
        open={alertMessage !== null}
        message={alertMessage ?? ""}
        onClose={() => setAlertMessage(null)}
      />

      {/* 通知先未選択の確認 */}
      <ConfirmDialog
        open={emptyNoticeConfirm != null}
        title="通知先が未選択です"
        description="メール通知先が選択されていません。このまま送信しますか？"
        confirmText="送信する"
        cancelText="キャンセル"
        onConfirm={() => {
          const kind = emptyNoticeConfirm;
          setEmptyNoticeConfirm(null);
          if (kind === "top") {
            void submitTop({ skipConfirm: true });
          } else if (kind === "thread") {
            void submitThreadReply({ skipConfirm: true });
          }
        }}
        onCancel={() => setEmptyNoticeConfirm(null)}
        onClose={() => setEmptyNoticeConfirm(null)}
        processing={
          emptyNoticeConfirm === "top" ? isPosting : isReplyPosting
        }
        disableRestoreFocus
      />

      {/* 削除確認モーダル */}
      <Dialog
        open={deleteState.open}
        onClose={deleteState.isSubmitting ? undefined : closeDeleteConfirm}
        aria-labelledby="delete-dialog-title"
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle id="delete-dialog-title" sx={{ fontWeight: 700 }}>
          メッセージを削除する
        </DialogTitle>
        <DialogContent dividers>
          <Typography sx={{ mb: 1.5 }}>
            このメッセージを本当に削除しますか？削除後は元に戻すことはできません。
          </Typography>

          {/* プレビュー */}
          {deleteState.preview && (
            <Card
              variant="outlined"
              sx={{
                borderRadius: 2,
                bgcolor: isDark ? "#243044" : "#fafafa",
              }}
            >
              <CardContent sx={{ py: 1.25 }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Avatar sx={{ width: 28, height: 28 }}>
                    {displayUserInitial(deleteState.preview.user)}
                  </Avatar>
                  <Typography fontWeight={700} variant="body2">
                    {deleteState.preview.user}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ ml: "auto" }}
                  >
                    {fmt(deleteState.preview.ts)}
                  </Typography>
                </Stack>
                <Typography
                  variant="body2"
                  sx={{
                    mt: 0.75,
                    whiteSpace: "pre-wrap",
                    overflowWrap: "anywhere",
                    wordBreak: "break-word",
                    lineHeight: 1.7,
                  }}
                >
                  {deleteState.preview.text}
                </Typography>
              </CardContent>
            </Card>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            variant="outlined"
            onClick={closeDeleteConfirm}
            disabled={deleteState.isSubmitting}
          >
            キャンセル
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={performDelete}
            disabled={deleteState.isSubmitting}
          >
            {deleteState.isSubmitting ? "削除中…" : "削除する"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};


/* ------------------------------------------------------
 * 通知先表示（投稿者名の横に小さく）
 * ---------------------------------------------------- */
const NoticeUsersInline: React.FC<{
  noticeUsers?: NoticeUser[];
}> = ({ noticeUsers }) => {
  const list = Array.isArray(noticeUsers) ? noticeUsers : [];
  if (list.length === 0) return null;

  return (
    <Stack
      direction="row"
      spacing={0.75}
      sx={{ ml: 1, flexWrap: "wrap", alignItems: "center" }}
    >
      {list.map((u, idx) => {
        const isConfirmed = Number(u.confirm) === 1;
        const status = getNoticeStatus(u.mail_status, isConfirmed);

        // 名前にマウスオーバーで詳細（3アイコン+状態）を表示
        return (
          <Tooltip
            key={`${u.user}-${idx}`}
            title={renderStatusDetail(u.user, status)}
            slotProps={{
              tooltip: {
                sx: {
                  bgcolor: "background.paper",
                  color: "text.primary",
                  border: 1,
                  borderColor: "divider",
                  boxShadow: 2,
                  maxWidth: 360,
                },
              },
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                px: 0.6,
                py: 0.2,
                border: 1,
                borderColor: "divider",
                borderRadius: 1,
                bgcolor: "background.paper",
                "&:hover": { borderColor: ACTIVE_COLOR },
              }}
            >
              <Typography variant="caption" color="text.secondary">
                {u.user}
              </Typography>
              {renderStatusCheck(isConfirmed, status.hasError)}
            </Box>
          </Tooltip>
        );
      })}
    </Stack>
  );
};

/* ------------------------------------------------------
 * 返信先の読み込み中表示（ユーザー一覧の取得中、返信先欄に小さく出す）
 * ---------------------------------------------------- */
const MentionLoadingIndicator: React.FC = () => (
  <Stack direction="row" alignItems="center" spacing={0.5} sx={{ height: 28 }}>
    <CircularProgress size={12} />
    <Typography variant="caption" color="text.secondary">
      返信先を読み込み中…
    </Typography>
  </Stack>
);

/* ------------------------------------------------------
 * メッセージカード（親コメント）
 * ---------------------------------------------------- */
type MessageCardProps = {
  node: ThreadNode;
  currentUserEmail: string;
  isActive: boolean; // 右ペインで開いているスレッドか
  onOpenThread: (id: number) => void;
  onOpenMenu: (
    e: React.MouseEvent<HTMLElement>,
    id: number,
    chatId?: number | null
  ) => void;
  isEditing: boolean;
  editingText: string;
  onChangeEditingText: (value: string) => void;
  onSaveEdit: () => void;
  onCancelEdit: () => void;
};

const MessageCard: React.FC<MessageCardProps> = ({
  node,
  currentUserEmail,
  isActive,
  onOpenThread,
  onOpenMenu,
  isEditing,
  editingText,
  onChangeEditingText,
  onSaveEdit,
  onCancelEdit,
}) => {
  // 自分の id を chatId として渡して、そのスレッドの返信数だけ数える
  const replyCount = countReplies(node, node.id);
  const replyLabel =
    replyCount === 0
      ? "返信"
      : replyCount === 1
      ? "1件の返信"
      : `${replyCount}件の返信`;

  const canEdit = emailEq(node.mail, currentUserEmail);

  return (
    <Card
      variant="outlined"
      data-chat-id={node.id}
      sx={{
        mb: 1,
        borderRadius: 2,
        position: "relative",
        "&:hover .message-menu-button": { opacity: canEdit ? 1 : 0 },
        // 右ペインで開いているスレッドは枠と背景で強調（枠の太さはレイアウトがずれないよう boxShadow で足す）
        ...(isActive && {
          borderColor: "primary.main",
          boxShadow: (theme) => `0 0 0 1px ${theme.palette.primary.main}`,
          bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04),
        }),
      }}
    >
      <CardContent sx={{ pb: 1.5 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
          <Avatar sx={{ width: 28, height: 28 }}>
            {displayUserInitial(node.user)}
          </Avatar>
          <Typography fontWeight={700} variant="body2">
            {node.user}
          </Typography>
          <NoticeUsersInline noticeUsers={node.noticeUsers} />
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ ml: "auto" }}
          >
            {fmt(node.ts)}
          </Typography>
        </Stack>

        {isEditing ? (
          <>
            <TextField
              id="editTopMessage"
              name="editTopMessage"
              fullWidth
              multiline
              minRows={2}
              value={editingText}
              onChange={(e) => onChangeEditingText(e.target.value)}
              sx={{ mt: 0.5 }}
            />
            <Stack
              direction="row"
              spacing={1}
              sx={{ mt: 1, justifyContent: "flex-end" }}
            >
              <Button
                size="small"
                variant="contained"
                color="success"
                onClick={onSaveEdit}
              >
                保存
              </Button>
              <Button size="small" variant="outlined" onClick={onCancelEdit}>
                キャンセル
              </Button>
            </Stack>
          </>
        ) : (
          <Box
            sx={{
              mt: 0.5,
              display: "flex",
              alignItems: "flex-start",
            }}
          >
            <Typography
              variant="body2"
              sx={{
                whiteSpace: "pre-wrap",
                overflowWrap: "anywhere",
                wordBreak: "break-word",
                lineHeight: 1.7,
                fontSize: 16,
                flexGrow: 1,
                minWidth: 0,
              }}
            >
              {node.text}
            </Typography>

            {canEdit && (
              <IconButton
                size="small"
                className="message-menu-button"
                onClick={(e) => onOpenMenu(e, node.id)}
                sx={{ opacity: 0, transition: "opacity 0.2s" }}
                aria-label="メニューを開く"
              >
                <MoreVertIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        )}

        <Stack
          direction="row"
          spacing={1}
          sx={{ mt: 1, alignItems: "center" }}
        >
          <Button
            size="small"
            variant="text"
            onClick={() => onOpenThread(node.id)}
          >
            {replyLabel}
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default WeeklyThreadWindow;
